// @flow
import _ from "lodash"
import moment from "moment"
import { Map } from "immutable"
import Booking from "@kiwicom/frontend-app/scripts/containers/mutable/Booking"
import Customer from "@kiwicom/frontend-app/scripts/containers/mutable/Customer"

import { ADDITIONAL_SERVICES_ALLOWED_TO_CHANGE_CONFIG } from "mmb/consts/additionalServices"
import mapperAdditionalServices from "./services/mapperAdditionalServices"
import mapperBagParams from "common/services/bags/mapperBagParams"
import mapperBookingPendingRefunds from "./services/mapperBookingPendingRefunds"
import type { DocumentNeed } from "common/services/document/mapperDocumentNeed"
import mapperDocumentNeed from "common/services/document/mapperDocumentNeed"
import mapperFlightsHistory from "./services/mapperFlightsHistory"
import mapperPendingFlightChangeOptions from "./services/mapperPendingFlightChangeOptions"
import mapperJourney from "common/services/journey/mapperJourney"
import mapperPendingRequests from "./services/mapperPendingRequests"
import mapperPassengers from "./services/mapperPassengers"
import mapperScheduleChange from "./services/mapperScheduleChange"
import mapperRefundMethods from "./services/mapperRefundMethods"
import type { BookingInput } from "../../../../../../records/bookingInput"
import { BAGS_TYPES, hasPaidHandBagsAllowedFromPassengers } from "../bags/tools"
import mapperInfantsConditions from "common/services/infants/mapperInfantsConditions"
import { getAirlines } from "common/services/staticData"
import type { Airlines } from "common/records/Airline"
import mapperBookingRequests from "./services/mapperBookingRequests"
import Price from "common/records/Price"
import { BAGS_MARGIN_CONST } from "booking/services/reservation/constants/marginConsts"
import mapperServicePackage from "./services/mapperServicePackage"
import mapperBaggages from "./services/mapperBaggages"
import mapperPayment from "./services/mapperPayment"
import mapperCheckIn from "./services/mapperCheckIn"
import mapperMeals from "./services/mapperMeals"

const COMPLETION_REAL_DEADLINE = 24

export type Config = {
  allowedToChangeSegments: boolean,
  allowedToChange: {
    // all values in hours
    additionalServices: number,
    bags: number,
    flights: number,
    insuranceAdd: number,
    insuranceChange: number,
    other: number,
    documents: number,
    document: {
      need: DocumentNeed,
    },
  },
}

export default function mapperManageBooking(
  input: BookingInput,
  // NOTE!!!
  // This is a terrible idea and only here as a god damn hotfix
  // If I see anyone else doing this shit he's gonna have to explain himself
  // in front of every FE dev eye to eye as a shaming ritual
  airlines: Airlines = getAirlines(window.__INITIAL_STATE__),
): Booking {
  // TODO: Use in more conditions, all data doesn't need to be parsed on account overview
  const isFullInput = typeof input.config !== "undefined"
  const assets = {}
  assets.boardingPasses =
    input.assets && input.assets.boarding_passes ? input.assets.boarding_passes : {}
  assets.eticket = input.assets && input.assets.eticket
  assets.transferTicket = input.assets && input.assets.transfer_ticket
  assets.insurance = input.assets && input.assets.insurance
  assets.confirmationEmailInEnglish = input.assets && input.assets.en_confirmation_link

  const journey = mapperJourney(input, "", false, airlines)
  const journeyWithHidden = mapperJourney(input, "", true, airlines)

  const customer = new Customer({
    phone: input.contact.phone,
    email: input.contact.email,
    pid: input.contact.pid,
  })

  // todo: remove
  const infantsConditions = mapperInfantsConditions(input.infants_conditions)

  const passengers = mapperPassengers(
    input.passengers,
    input.travel_info,
    input.infants_conditions
      ? infantsConditions.get("handWeight") > 0
      : journey.handBagsAllowedForInfants(), // keep backward compatibility
  )

  const actions = {}
  if (input.assets) {
    actions.rateLow = input.assets.lowRating
    actions.rateHigh = input.assets.highRating
    actions.fillDetails = input.assets.documentDetails
  }

  let config
  if (input.config) {
    config = {
      allowedToChangeSegments: _.get(input, "config.allowed_to_change_segments", true), // defaults to true, as this field is new in api
      allowedToChange: {
        // all values in hours
        bags: input.config.allowed_to_change.bags,
        flights: input.config.allowed_to_change.flights,
        insuranceAdd: input.config.allowed_to_change.insurance_add,
        insuranceChange: input.config.allowed_to_change.insurance_change,
        other: input.config.allowed_to_change.other,
        assistedRefund: input.config.allowed_to_change.assisted_refund,
        documents: COMPLETION_REAL_DEADLINE, // temporarily hardcoded --- input["config"]["allowed_to_change"]["documents"],
        additionalServices: ADDITIONAL_SERVICES_ALLOWED_TO_CHANGE_CONFIG,
      },
      document: {
        need: mapperDocumentNeed(input.document_options.document_need),
      },
    }
  }

  let pendingRefunds = []
  if (_.isArray(input.pending_refunds)) {
    pendingRefunds = mapperBookingPendingRefunds(input.pending_refunds)
  }

  // TODO: temporary, until we agree with backend
  let insurancePricing
  if (input.insurance_price) {
    insurancePricing = {
      travel_basic: input.insurance_price.travel_basic,
      travel_plus: input.insurance_price.travel_plus,
    }
  }

  // voluntary only
  let refundOptions
  if (input.refundable_options) {
    refundOptions = {
      requested: input.refundable_options.requested
        ? moment.utc(input.refundable_options.requested * 1000)
        : null,
      validUntil:
        input.refundable_options.refundable_until &&
        moment.utc(input.refundable_options.refundable_until * 1000),
    }
  } else {
    refundOptions = {
      requested: null,
      validUntil: null,
    }
  }

  // grace period only
  let refundOption
  if (_.isArray(input.refund_options)) {
    refundOption = input.refund_options.map(x => ({
      validUntil: moment.utc(x.valid_until * 1000),
    }))[0]
  } else {
    refundOption = null
  }

  // We have to map params in "paid hand bags" way even if pax did not buy any paid hand bags (if it was possible - Frontier)
  const hasPaidHandBagsAllowed = hasPaidHandBagsAllowedFromPassengers(passengers, journey)
  // Billing details data
  let billingDetails
  if (input.billing_details) {
    billingDetails = {}
    Object.keys(input.billing_details).forEach(k => {
      billingDetails[_.camelCase(k)] = input.billing_details[k]
    })
  }

  let bagParams = mapperBagParams(
    input.bag_params,
    Map(input.bags_restrictions),
    hasPaidHandBagsAllowed,
    infantsConditions.get("differentHandWeight"),
    infantsConditions.get("handWeight"),
  )

  // temporary fix - FR priority boarding
  if (!bagParams.get(BAGS_TYPES.HAND).get("length")) {
    const cabinBag = _.get(input, ["baggages", "confirmed"], []).find(
      baggage => baggage.bag.category === "cabin_bag",
    )

    if (cabinBag) {
      const oldHand = bagParams.get(BAGS_TYPES.HAND)
      bagParams = bagParams.set(BAGS_TYPES.HAND, oldHand.merge(cabinBag.bag))
    }
  }

  const additionalBagsPrices = _.has(input, "bags_price_split")
    ? _.mapValues(
        _.get(input, "bags_price_split"),
        value =>
          new Price({
            amount: _.get(value, "amount"),
            base: _.get(value, "base"),
            service: _.get(value, "service"),
            serviceFlat: _.get(value, "service_flat"),
            merchant: _.get(value, "merchant"),
            currency: _.get(value, "currency"),
          }),
      )
    : _.mapValues(
        _.get(input, "additional_bags_prices"),
        value =>
          new Price({
            amount: value,
            base: value * BAGS_MARGIN_CONST,
            service: value * (1 - BAGS_MARGIN_CONST),
          }),
      )

  return new Booking({
    id: input.bid,
    created: input.created * 1000,
    status: input.status,
    blocked: input.blocked,
    ticketing_allowed: input.ticketing_allowed,
    authToken: input.auth_token,
    additionalContacts: input.additional_contacts || null,
    config,
    journey,
    journeyWithHidden,
    additionalBagsPrices,
    bookingChanges: mapperBookingRequests(input.passenger_changes) || [],
    insurancePricing,
    flightsHistory: mapperFlightsHistory(input.flights_history),
    meals: mapperMeals(input.meals),
    additionalServices: mapperAdditionalServices(input),
    pendingFlightChangeOptions: mapperPendingFlightChangeOptions(
      input.pending_flight_changes,
      airlines,
    ),
    pendingRequests: mapperPendingRequests(input.pending_requests),
    bagParams,
    bags: input.bags,
    baggages: mapperBaggages(input.baggages),
    actions,
    assets,
    customer,
    passengers,
    infantsConditions,
    sandbox: !!input.sandbox,
    payment: isFullInput
      ? mapperPayment({
          original_price: input.original_price,
          credits_payment: input.credits_payment,
          card_payment: input.card_payment,
          original_currency: input.original_currency,
          price: input.price,
        })
      : undefined, // Undefined is absence of evidence
    cancelRequested: input.cancel_requested ? input.cancel_requested * 1000 : null,
    pendingRefund: pendingRefunds.filter(
      // temporary calculator condition - remove after
      x => x.status === "pending" && x.reason !== "voluntary",
    )[0],
    refundOption, // grace period only
    refundOptions, // voluntary cancellation only
    refundMethods: mapperRefundMethods(input.refund_methods),
    scheduleChange: input.schedule_change ? mapperScheduleChange(input.schedule_change) : null,
    billingDetails,
    servicePackage: mapperServicePackage(input.service_package),
    paxAta: _.get(input, ["pax_ata", "href"]),
    checkIn: mapperCheckIn(input.checkin),
  })
}
